<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1100px"
    persistent
    scrollable
    data-testid="tq2-history-compare-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-compare</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ titleText }}</div>
          <div class="text-caption text-white-50">เทียบชุดคำถามของ 2 รอบบันทึก (ซ้าย = รอบเก่ากว่า)</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="tq2-compare-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="tq2-compare-error"
        >
          {{ error }}
        </v-alert>

        <div v-if="loading" class="text-center py-10" data-testid="tq2-compare-loading">
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="text-caption text-medium-emphasis mt-3 mb-0">กำลังโหลดข้อมูล 2 รอบเพื่อเปรียบเทียบ…</p>
        </div>

        <template v-else-if="diff">
          <div class="d-flex flex-wrap align-center ga-2 mb-4" data-testid="tq2-compare-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ diff.summary.added }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-minus">
              ลบ {{ diff.summary.removed }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ diff.summary.modified }}
            </v-chip>
            <v-chip variant="tonal" size="small" prepend-icon="mdi-equal">
              เหมือนเดิม {{ diff.summary.unchanged }}
            </v-chip>
            <v-spacer />
            <v-btn-toggle
              v-model="changedOnly"
              color="primary"
              density="comfortable"
              variant="outlined"
              mandatory
              data-testid="tq2-compare-filter"
            >
              <v-btn :value="true" size="small">แสดงเฉพาะที่เปลี่ยน</v-btn>
              <v-btn :value="false" size="small">แสดงทั้งหมด</v-btn>
            </v-btn-toggle>
          </div>

          <div v-for="group in groups" :key="group.name" class="mb-5">
            <div class="text-subtitle-2 font-weight-medium mb-2">
              {{ group.title }}
              <span class="text-caption text-medium-emphasis">({{ group.rows.length }})</span>
            </div>

            <p v-if="!group.rows.length" class="text-caption text-medium-emphasis mb-0">
              {{ changedOnly ? 'ไม่มีการเปลี่ยนแปลงในกลุ่มนี้' : 'ไม่มีข้อมูลในกลุ่มนี้' }}
            </p>

            <v-expansion-panels v-else variant="accordion" multiple :data-testid="group.testid">
              <v-expansion-panel v-for="row in group.rows" :key="row.key">
                <v-expansion-panel-title>
                  <div class="d-flex align-center flex-wrap ga-2 w-100">
                    <v-icon :color="stateColor(row.state)" size="18">{{ stateIcon(row.state) }}</v-icon>
                    <span class="font-weight-medium">{{ row.label || '(ไม่มีชื่อ)' }}</span>
                    <v-chip v-if="row.itemKind" size="x-small" variant="tonal">{{ row.itemKind }}</v-chip>
                    <v-chip v-if="row.stepCode" size="x-small" variant="tonal">{{ row.stepCode }}</v-chip>
                    <v-chip
                      v-if="row.groupCode && row.groupCode !== row.stepCode"
                      size="x-small"
                      variant="text"
                      class="text-medium-emphasis"
                    >
                      {{ row.groupCode }}
                    </v-chip>
                    <v-spacer />
                    <v-chip size="x-small" variant="tonal" :color="stateColor(row.state)">
                      {{ stateLabel(row.state) }}
                      <template v-if="row.fields.length"> · {{ row.fields.length }} ฟิลด์</template>
                    </v-chip>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p v-if="!row.fields.length" class="text-caption text-medium-emphasis mb-0">
                    ไม่มีการเปลี่ยนแปลง
                  </p>
                  <v-table v-else density="compact" class="tq2-compare-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 220px">ฟิลด์</th>
                        <th class="text-left">v{{ leftNo }}</th>
                        <th class="text-left">v{{ rightNo }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in row.fields" :key="row.key + '|' + f.name">
                        <td class="font-weight-medium">{{ f.name }}</td>
                        <td class="tq2-compare-value">{{ showValue(f.left) }}</td>
                        <td class="tq2-compare-value">{{ showValue(f.right) }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" color="primary" data-testid="tq2-compare-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Compare two save-history snapshots of ONE Tax Question V2 version, side by side.
 *
 * The parent (the history dialog) hands over the two ticked `snapshotID`s in whatever order the
 * admin ticked them; this dialog fetches both payloads in parallel and puts the LOWER
 * `snapshotNo` on the left, so the table always reads older → newer no matter how it was opened.
 *
 * All diffing is client-side (`utils/taxQuestionSnapshotDiff.js`) — there is no compare endpoint.
 * That util compares the UNION of the keys on both sides, so a column added to the snapshot
 * payload later shows up here with no change to this file.
 */
import taxQuestionV2Api from '@/services/taxQuestionV2Api';
import { diffSnapshots, truncateValue } from '@/utils/taxQuestionSnapshotDiff';

const STATE_ICON = {
  added: 'mdi-plus',
  removed: 'mdi-minus',
  modified: 'mdi-pencil',
  unchanged: 'mdi-equal',
};
const STATE_COLOR = {
  added: 'success',
  removed: 'error',
  modified: 'primary',
  unchanged: 'grey',
};
const STATE_LABEL = {
  added: 'เพิ่ม',
  removed: 'ลบ',
  modified: 'แก้',
  unchanged: 'เหมือนเดิม',
};

export default {
  name: 'TaxQuestionHistoryCompareV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** snapshotID of one ticked row (either side — sorted by snapshotNo on load) */
    leftSnapshotId: { type: Number, default: null },
    /** snapshotID of the other ticked row */
    rightSnapshotId: { type: Number, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      loading: false,
      error: '',
      diff: null,
      leftNo: '',
      rightNo: '',
      changedOnly: true,
    };
  },
  computed: {
    titleText() {
      if (this.leftNo === '' || this.rightNo === '') return 'เทียบ 2 รอบบันทึก';
      return `เทียบ v${this.leftNo} ↔ v${this.rightNo}`;
    },
    groups() {
      const d = this.diff;
      return [
        {
          name: 'steps',
          title: 'Sections',
          testid: 'tq2-compare-steps',
          rows: this.visible(d ? d.steps : []),
        },
        {
          name: 'items',
          title: 'Questions',
          testid: 'tq2-compare-items',
          rows: this.visible(d ? d.items : []),
        },
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
      else this.reset();
    },
  },
  methods: {
    stateIcon(state) {
      return STATE_ICON[state] || 'mdi-help';
    },
    stateColor(state) {
      return STATE_COLOR[state] || 'grey';
    },
    stateLabel(state) {
      return STATE_LABEL[state] || state;
    },
    /** Empty string on a side means «this side has no value» — say so instead of showing a gap. */
    showValue(v) {
      const s = truncateValue(v);
      return s === '' ? '—' : s;
    },
    visible(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return this.changedOnly ? list.filter((r) => r.state !== 'unchanged') : list;
    },
    reset() {
      this.diff = null;
      this.error = '';
      this.leftNo = '';
      this.rightNo = '';
      this.changedOnly = true;
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async load() {
      this.reset();
      if (!this.leftSnapshotId || !this.rightSnapshotId) {
        this.error = 'ต้องเลือก 2 รอบเพื่อเปรียบเทียบ';
        return;
      }
      this.loading = true;
      try {
        const [a, b] = await Promise.all([
          taxQuestionV2Api.getSnapshot(this.leftSnapshotId),
          taxQuestionV2Api.getSnapshot(this.rightSnapshotId),
        ]);
        // Older round on the left, whatever order the checkboxes were ticked in.
        const older = (a.snapshotNo ?? 0) <= (b.snapshotNo ?? 0) ? a : b;
        const newer = older === a ? b : a;
        this.leftNo = older.snapshotNo;
        this.rightNo = newer.snapshotNo;
        this.diff = diffSnapshots(older.payloadJson, newer.payloadJson);
      } catch (e) {
        this.diff = null;
        this.error = (e && e.message) || 'โหลดข้อมูลเปรียบเทียบไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.tq2-compare-table :deep(td),
.tq2-compare-table :deep(th) {
  vertical-align: top;
}
.tq2-compare-value {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 380px;
}
</style>
